import BigNumber from 'bignumber.js';
import { ActionCableService, distinctBigNumberUntilChanged, sharePublishReplay, swalloError } from 'ngx-libs';
import { BehaviorSubject, combineLatest, Observable, of, timer } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { AccountService } from '../services/account.service';
import { ContractService } from '../services/contract.service';
import { EthWalletService } from '../services/eth-wallet.service';
import { MainService } from '../services/main.service';
import { Base } from './base';
import { Market } from './market';
import { PoolToken } from './pool-token';
import { MAX_INT, Vault } from './vault/vault';
import { environment } from 'src/environments/environment';
import { PaymasterMethod } from './paymaster-method';

const greeks = require('greeks');
export class Pool extends Base {
  id: number;
  name: string;
  description: string;
  apy: number;
  avgApy: number;
  type: string;
  createdAt: number;
  profit30D: number;

  token: Observable<PoolToken>;
  quoteApproved: Observable<boolean>;
  quoteAllowance: Observable<BigNumber>;

  marginBalance: Observable<BigNumber>;
  equity: Observable<BigNumber>;
  equityDisplay: Observable<string>;
  upnl: Observable<BigNumber>;
  available: Observable<BigNumber>;
  realAvailable: Observable<BigNumber>;
  healthFactor: Observable<number>;
  totalShares: Observable<BigNumber>;
  priceOfShare: Observable<BigNumber>;
  utilization: Observable<number>;
  realUtilization: Observable<number>;
  myShares: Observable<BigNumber>;
  myValue: Observable<BigNumber>;
  positions: Observable<any[]>;
  staked: Observable<boolean>;
  reservedRate: Observable<BigNumber>;
  withdrawFeeRate: Observable<BigNumber>;
  netDelta: Observable<BigNumber>;
  myNetDelta: Observable<BigNumber>;
  snapshots: Observable<any[]>;
  allApy: Observable<number>;
  last30dApy: Observable<number>;
  costPerShare: Observable<BigNumber>;

  expanded = false;
  toggling = false;

  private dataSubject = new BehaviorSubject<any>({});

  constructor(
    public contract: string,
    protected abi: any,
    protected accountService: AccountService,
    protected contractService: ContractService,
    private ethWalletService: EthWalletService,
    private actionCableService: ActionCableService,
    private mainService: MainService,
    public market: Market
  ) {
    super(contract, abi, accountService, contractService);

    this.reservedRate = this.dataSubject.pipe(map((data) => data.reservedRate));
    this.withdrawFeeRate = this.dataSubject.pipe(map((data) => data.withdrawFeeRate));

    const balance = this.market.vault.chain.getBalanceOfCache(this.contract);
    const vaultSource = combineLatest([balance, timer(0, 60000)]).pipe(sharePublishReplay(1));
    const token = this.dataSubject.pipe(
      map((data) => data.token),
      distinctUntilChanged()
    );

    this.token = token.pipe(
      filter((t) => t),
      take(1),
      map(
        (t) =>
          new PoolToken(
            t,
            this.contract,
            18,
            this.accountService,
            this.ethWalletService,
            this.contractService,
            this.actionCableService,
            this.mainService
          )
      ),
      sharePublishReplay(1)
    );

    this.totalShares = combineLatest([this.dataSubject, this.market.pools]).pipe(
      debounceTime(10),
      map(([data]) => data.totalShares)
    );

    this.positions = vaultSource.pipe(
      switchMap(() => this.market.vault.getPositions(this.contract).pipe(swalloError())),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      sharePublishReplay(1)
    );

    const accountInfo = vaultSource.pipe(
      switchMap(() => this.market.vault.getAccountInfo(this.contract)),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      sharePublishReplay(1)
    );

    this.marginBalance = accountInfo.pipe(
      map((a) => a.marginBalance || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    this.equity = accountInfo.pipe(
      map((a) => a.equity || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    this.equityDisplay = this.equity.pipe(
      map((e) => {
        let value = e.toNumber();
        let u = '';
        if (value > 1000000) {
          value /= 1000000;
          u = 'm';
        } else if (value > 1000) {
          value /= 1000;
          u = 'k';
        }
        return `$${value.toFixed(1)}${u}`;
      })
    );

    this.upnl = accountInfo.pipe(
      map((a) => a.upnl || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    const available = accountInfo.pipe(
      map((a) => a.available || new BigNumber(0)),
      distinctBigNumberUntilChanged()
    );

    const reserved = combineLatest([this.marginBalance, this.reservedRate]).pipe(map(([m, r]) => m.times(r)));
    this.available = combineLatest([available, reserved]).pipe(map(([a, r]) => a.minus(r)));
    this.realAvailable = available;
    this.healthFactor = accountInfo.pipe(
      map((a) => {
        if (a.healthFactor) {
          return a.healthFactor.toDecimal().eq(MAX_INT) ? null : a.healthFactor.toNumber();
        } else {
          return null;
        }
      }),
      distinctUntilChanged()
    );

    this.utilization = combineLatest([this.equity, this.available, reserved]).pipe(
      debounceTime(0),
      map(([e, a, r]) => (e.lte(0) ? null : 1 - a.div(e.minus(r)).toNumber())),
      distinctUntilChanged()
    );

    this.realUtilization = combineLatest([this.equity, this.realAvailable]).pipe(
      debounceTime(0),
      map(([e, a]) => (e.lte(0) ? null : 1 - a.div(e).toNumber())),
      distinctUntilChanged()
    );

    this.priceOfShare = combineLatest([this.equity, this.totalShares]).pipe(
      map(([v, t]) => (t.eq(0) ? new BigNumber(0) : v.div(t).truncate(18)))
    );

    let approved = false;
    const account = this.accountService.account.pipe(
      distinctUntilChanged(),
      tap(() => {
        approved = false;
      })
    );

    this.quoteAllowance = account.pipe(
      switchMap((a) =>
        timer(0, 5000).pipe(
          filter(() => !approved),
          switchMap(() =>
            a ? this.market.quote.allowance(a, this.contract).pipe(swalloError()) : of(new BigNumber(0))
          )
        )
      ),
      distinctBigNumberUntilChanged()
    );

    this.quoteApproved = this.quoteAllowance.pipe(
      map((v) => v.gt(0)),
      tap((r) => {
        approved = r;
      }),
      sharePublishReplay(1)
    );

    this.myShares = this.token.pipe(switchMap((t) => t.balance));

    this.myValue = combineLatest([this.priceOfShare, this.myShares]).pipe(
      map(([price, shares]) => price.times(shares))
    );

    this.staked = this.myShares.pipe(map((s) => s.gt(0)));

    const spotPrice = this.market.spotPricer.price;
    this.netDelta = combineLatest([this.positions, spotPrice, this.equity]).pipe(
      map(([positions, sp]) => {
        const spot = sp.toNumber();
        return new BigNumber(
          positions.reduce((netDelta, position) => {
            const iv = this.market.contractVault.optionMarket
              .getIv(position.expiry, position.strike.toDecimal(), position.isCall, position.size.gt(0))
              .fromDecimal()
              .toNumber();
            const time = (position.expiry - Date.now() / 1000) / 31536000;
            const delta = greeks.getDelta(
              spot,
              position.strike.toNumber(),
              time,
              iv,
              0.06,
              position.isCall ? 'call' : 'put'
            );
            return delta * position.size.toNumber() + netDelta;
          }, 0)
        );
      }),
      sharePublishReplay(1)
    );

    this.myNetDelta = combineLatest([this.myShares, this.totalShares, this.netDelta]).pipe(
      map(([s, t, n]) => s.div(t).times(n))
    );

    this.snapshots = timer(0, 60000).pipe(
      switchMap(() => this.api.poolSnapshots.query().pipe(swalloError())),
      map((json) => json.poolSnapshots),
      sharePublishReplay(1)
    );

    this.allApy = this.priceOfShare.pipe(
      map((p) => {
        const timeRatio = (365 * 86400000) / (Date.now() - this.createdAt * 1000);
        return p.eq(0) ? 0 : p.toNumber() ** timeRatio - 1;
      })
    );

    this.last30dApy = timer(0, 60000).pipe(
      mergeMap(() => {
        if (Date.now() / 1000 - this.createdAt < 86400 * 30) {
          return this.allApy;
        } else {
          const apy = Math.max(this.avgApy, this.apy);
          return of(apy > 0 ? apy : apy / 12);
        }
      })
    );

    this.costPerShare = combineLatest([this.accountService.account, balance]).pipe(
      debounceTime(300),
      switchMap(([a]) =>
        a ? this.market.api.accounts.of(a).pools.of(this.contract).funds.getTotal() : of({})
      ),
      map((result) => result.price),
      sharePublishReplay(1)
    );
  }

  get api() {
    return this.market.api.pools.of(this.id.toString());
  }

  updateData(data: any) {
    this.market.contractVault.config.setPoolReservedRate(this.contract, data.reservedRate.toDecimal());
    this.market.contractVault.config.setPoolPaused(this.contract, data.paused);
    this.dataSubject.next(data);
  }

  getToken() {
    return this.call((i) => i.methods.token());
  }

  approveQuote(paymasterMethod?: PaymasterMethod) {
    return this.market.quote.approveAll(this.contract, paymasterMethod);
  }

  tryDeposit(amount: any, paymasterMethod?: PaymasterMethod) {
    if (environment.features.signedData) {
      return this.market.signedData.pipe(
        take(1),
        mergeMap((json) =>
          this.estimateGas((i) => i.methods.deposit(new BigNumber(amount).toDecimal().toString(10)), {
            paymasterMethod,
            extraData: json.data
          })
        )
      );
    } else {
      return this.estimateGas((i) => i.methods.deposit(new BigNumber(amount).toDecimal().toString(10)));
    }
  }

  deposit(amount: any, paymasterMethod?: PaymasterMethod) {
    if (environment.features.signedData) {
      return this.market.api.oracles.getSignedData().pipe(
        mergeMap((json) =>
          this.send((i) => i.methods.deposit(new BigNumber(amount).toDecimal().toString(10)), {
            paymasterMethod,
            extraData: json.data
          })
        )
      );
    } else {
      return this.send((i) => i.methods.deposit(new BigNumber(amount).toDecimal().toString(10)));
    }
  }

  signWithdraw(
    shares: any,
    acceptableAmount: any,
    gasFee: BigNumber = null,
    deadline = Math.floor(Date.now() / 1000) + 300
  ) {
    shares = new BigNumber(shares).toDecimal().toString(10);
    acceptableAmount = new BigNumber(acceptableAmount).toDecimal().toString(10);

    const withdrawSignatures = this.api.withdrawSignatures;
    return this.accountService.account.pipe(
      take(1),
      mergeMap((account) => {
        const gasFeeObservable = gasFee
          ? of(gasFee.toDecimal().toString(10))
          : withdrawSignatures
              .getGas({ data: { shares, acceptable_amount: acceptableAmount }, deadline, account })
              .pipe(map((json: any) => json.gas));

        return combineLatest([
          gasFeeObservable,
          withdrawSignatures.getNonce(account.toLowerCase())
        ]).pipe(
          take(1),
          mergeMap(([gasFee2, nonceJson]: [any, any]) => {
            const nonce = nonceJson.nonce;
            const message = { shares, acceptableAmount, deadline, gasFee: gasFee2, nonce };
            const types = this.createTypes({
              Withdraw: [
                { name: 'shares', type: 'uint256' },
                { name: 'acceptableAmount', type: 'uint256' },
                { name: 'deadline', type: 'uint256' },
                { name: 'gasFee', type: 'uint256' },
                { name: 'nonce', type: 'uint256' }
              ]
            });
            account = account.toLowerCase();

            const msgParams = {
              types,
              domain: this.market.domain,
              primaryType: 'Withdraw',
              message
            };

            return this.ethWalletService.signTypedData(account, msgParams).pipe(
              mergeMap((vrs) =>
                withdrawSignatures.create({
                  account,
                  nonce,
                  deadline,
                  ...vrs,
                  gasFee: gasFee2,
                  data: { shares, acceptable_amount: acceptableAmount }
                })
              )
            );
          })
        );
      })
    );
  }

  withdraw(shares: any, acceptableAmount: any) {
    if (environment.features.signedData) {
      return this.market.api.oracles
        .getSignedData()
        .pipe(
          mergeMap((json) =>
            this.send(
              (i) =>
                i.methods.withdraw(
                  new BigNumber(shares).toDecimal().toString(10),
                  new BigNumber(acceptableAmount).toDecimal().toString(10)
                ),
              { extraData: json.data }
            )
          )
        );
    } else {
      return this.send((i) =>
        i.methods.withdraw(
          new BigNumber(shares).toDecimal().toString(10),
          new BigNumber(acceptableAmount).toDecimal().toString(10)
        )
      );
    }
  }

  tryWithdraw(shares: any, acceptableAmount: any, deadline = Math.floor(Date.now() / 1000) + 300) {
    const args: any[] = [
      new BigNumber(shares).toDecimal().toString(10),
      new BigNumber(acceptableAmount).toDecimal().toString(10)
    ];
    if (this.type === 'v2') {
      args.push(deadline);
    }
    if (environment.features.signedData) {
      return this.market.signedData.pipe(
        take(1),
        mergeMap((json: any) => this.estimateGas((i) => i.methods.withdraw(...args), { extraData: json.data })),
        this.catchError()
      );
    } else {
      return this.estimateGas((i) => i.methods.withdraw(...args)).pipe(this.catchError());
    }
  }

  catchError() {
    return catchError((e) =>
      this.market.vault.instances.readonlyInstance.pipe(
        mergeMap((instance) => this.tryParseCustomError(e, [instance._errorsInterface]))
      )
    );
  }
}
